import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import {
  setPublicShareUuid,
  setShareTokens,
  setShareExpiry,
  getShareExpiry,
  subscribeShareExpiry,
  setInvitingAgent,
  setRecipientName,
  loadShareSessionToken,
} from './services/shareContext';
import {
  resolveShareLink,
  fetchExistingReveals,
  buildAgentPhotoUrl,
  PublicShareModule,
} from './services/superPickerPublicApi';
import SitePlansPage from './pages/SitePlansPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ProjectResearchPage from './pages/ProjectResearchPage';
import { RevealedPricesProvider, useRevealedPrices } from './services/revealedPrices';
import { FloorPlanCompareProvider } from './hooks/useFloorPlanCompare';
import FloorPlanCompareShell from './components/detail/unit/FloorPlanCompareShell';
import {
  initPublicAnalytics,
  setPublicAnalyticsShareContext,
  logShareLandingViewed,
} from './services/analytics';
import { OtpVerificationDialog } from './components/OtpVerificationDialog';
import { InvitingAgentCard } from './components/InvitingAgentCard';

/**
 * Public-facing Super Picker entry. Reachable at `/super-picker-public`.
 *
 * Two modes are supported:
 *
 *   1. **Agent EXTERNAL** — a logged-in agent with the `EXTERNAL` role on
 *      Super Picker. They land here from the agent shell; we probe
 *      `/api/super-picker-public/access` to confirm the role and then
 *      render `SitePlansPage` (the existing default landing UI).
 *
 *   2. **Share-link recipient** — the URL carries `?uuid=<UUID>` generated
 *      by an agent via the Share dialog. We resolve the uuid against
 *      `/api/super-picker-public/share/:uuid`, install it into
 *      `shareContext` so every downstream `publicFetch` call carries it,
 *      and route directly to the page the bound `Module` allows:
 *
 *        * `PICKER_SITE_PLANS`       → `SitePlansPage`
 *        * `PICKER_PROJECT_DETAILS`  → `ProjectDetailPage` for the bound projectId
 *        * `PICKER_PROJECT_RESEARCH` → `ProjectResearchPage`
 */
type AccessState =
  | { kind: 'checking' }
  | {
      kind: 'granted-share';
      uuid: string;
      module: PublicShareModule;
      projectId: string | null;
      mobileMasked: string | null;
      /**
       * `true` while the recipient has not yet completed OTP
       * verification. We still know the bound module/project (resolve
       * returns them so the OTP dialog can show context), but every
       * gated API call would 403, so the SPA overlays an `OtpVerificationDialog`
       * and hides page content behind it.
       */
      needsOtp: boolean;
    }
  | { kind: 'missing-uuid' }
  | { kind: 'expired' }
  | { kind: 'revoked' }
  | { kind: 'denied'; reason: string }
  /**
   * The share resolved successfully but the bound `Module` is not one
   * of the three modules this SPA knows how to render. Most likely
   * cause is a backend that has rolled out a new module value before
   * the frontend was updated. We surface this explicitly rather than
   * silently dropping the recipient onto the wrong page.
   */
  | { kind: 'unauthorized-module'; receivedModule: string };

/**
 * The exhaustive list of `Module` values this SPA knows how to render.
 * Kept in sync with `PublicShareModule` and the backend enum
 * `SUPER_PICKER_EXTERNAL_USER_MODULES`. Used at runtime to validate
 * the resolved share — TypeScript's static narrowing alone wouldn't
 * catch a backend that returned an unknown module string.
 */
const KNOWN_PUBLIC_MODULES: ReadonlySet<PublicShareModule> = new Set<PublicShareModule>([
  'PICKER_SITE_PLANS',
  'PICKER_PROJECT_DETAILS',
  'PICKER_PROJECT_RESEARCH',
]);
const isKnownPublicModule = (value: unknown): value is PublicShareModule =>
  typeof value === 'string' && KNOWN_PUBLIC_MODULES.has(value as PublicShareModule);

function getUuidFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('uuid');
  return raw && raw.trim().length > 0 ? raw.trim() : null;
}

/**
 * Wrapper that hosts cross-cutting providers (currently
 * `RevealedPricesProvider`, which owns the in-memory unmasked-price cache).
 * Mounted as the route element so every page below shares one cache.
 */
export function SuperPickerPublicApp() {
  return (
    <RevealedPricesProvider>
      <FloorPlanCompareProvider>
        <SuperPickerPublicAppInner />
        <FloorPlanCompareShell />
      </FloorPlanCompareProvider>
    </RevealedPricesProvider>
  );
}

function SuperPickerPublicAppInner() {
  const [state, setState] = useState<AccessState>({ kind: 'checking' });
  // Hydrate previously-revealed prices into the in-memory cache so a page
  // refresh doesn't re-mask everything the recipient already paid for.
  // The replay endpoint is free (no token charge); see the backend
  // `handleListExistingReveals`.
  const { seedRevealed } = useRevealedPrices();

  useEffect(() => {
    // Boot Firebase analytics once per page-load. Subsequent calls are
    // no-ops, so it's safe to re-invoke alongside the access-verify effect.
    initPublicAnalytics();

    let mounted = true;

    const verifyAccess = async () => {
      const uuid = getUuidFromUrl();

      // The public Super Picker app is share-link only — a visit without a
      // `?uuid=` query param has no way to be authorized. We do NOT fall
      // back to the agent-EXTERNAL probe anymore.
      if (!uuid) {
        if (mounted) setState({ kind: 'missing-uuid' });
        return;
      }

      try {
        const share = await resolveShareLink(uuid);
        if (!mounted) return;
        // Validate the bound module against the SPA's known set BEFORE
        // we initialise any downstream state (analytics, reveals,
        // expiry timers). An unrecognised module means we cannot
        // safely render any page for this share — surface a dedicated
        // unauthorized screen with the offending value rather than
        // silently falling back to Site Plans.
        if (!isKnownPublicModule(share.module)) {
          setState({
            kind: 'unauthorized-module',
            receivedModule: String(share.module ?? ''),
          });
          return;
        }
        setPublicShareUuid(share.uuid);
        setShareTokens({ remaining: share.remainingTokens, max: share.maxTokens });
        // Install the inviting agent BEFORE branching to any blocked
        // state so the revoked / expired screens have data for the
        // agent contact card. (Reassigned below as well — duplicate
        // assignment is harmless; the store dedupes by reference.)
        setInvitingAgent(
          share.invitedBy
            ? {
                ceaNumber: share.invitedBy.ceaNumber,
                name: share.invitedBy.name,
                email: share.invitedBy.email,
                mobile: share.invitedBy.mobile,
                photo: buildAgentPhotoUrl(share.invitedBy.photo) ?? null,
              }
            : null,
        );
        // Stash the recipient's display name so the exported PDF cover
        // can render a "Specially Prepared for <name>" block.
        setRecipientName(share.name);
        // Manually revoked → "link revoked" screen. Backend keeps the
        // 410 enforcement on every gated route so no data leaks even
        // if the SPA were bypassed.
        if (share.defunctAt) {
          setState({ kind: 'revoked' });
          return;
        }
        // Install the trial expiry into the shared store. `null` means
        // "never expires" (legacy rows without `CreatedAt`); the
        // PublicHeader chip and auto-kick timer below both handle this.
        const expiryMs = share.expiresAt ? Date.parse(share.expiresAt) : NaN;
        setShareExpiry(Number.isFinite(expiryMs) ? expiryMs : null);
        setInvitingAgent(
          share.invitedBy
            ? {
                ceaNumber: share.invitedBy.ceaNumber,
                name: share.invitedBy.name,
                email: share.invitedBy.email,
                mobile: share.invitedBy.mobile,
                photo: buildAgentPhotoUrl(share.invitedBy.photo) ?? null,
              }
            : null,
        );
        // Hydrate any previously-issued session JWT for this uuid from
        // `localStorage`. Per-device binding: if the share is already
        // activated but THIS browser has no token, the recipient is on
        // a different device than the one that first verified — we
        // re-prompt OTP so the holder of the bound WhatsApp number can
        // re-claim a session here. The backend's `send` / `verify`
        // routes both accept activated shares (re-verify preserves the
        // original `ActivatedAt`, so the trial cannot be extended).
        const hasLocalSession = loadShareSessionToken(share.uuid) != null;
        const needsOtp = share.activatedAt == null || !hasLocalSession;
        // Stash the share identity on the analytics module so every
        // downstream event (project_viewed, factsheet_viewed, etc.) is
        // implicitly attributable to this invite. We deliberately use
        // the *masked* mobile (e.g. `****1234`) the backend exposes —
        // the unmasked number stays server-side.
        setPublicAnalyticsShareContext({
          uuid: share.uuid,
          mobileMasked: share.mobileMasked,
        });
        // Only emit the "landing viewed" event once OTP is past — until
        // then the recipient hasn't really "entered" the page yet.
        if (!needsOtp) {
          logShareLandingViewed({
            module: share.module,
            projectId: share.projectId,
          });
        }
        setState({
          kind: 'granted-share',
          uuid: share.uuid,
          module: share.module,
          projectId: share.projectId,
          mobileMasked: share.mobileMasked,
          needsOtp,
        });
        // Skip the reveals-hydration round-trip when the share isn't
        // activated yet — the endpoint would 403 with `SHARE_NOT_ACTIVATED`
        // and we'd just clutter the console. The post-activation handler
        // below hydrates once the user verifies.
        if (!needsOtp) {
          fetchExistingReveals(share.uuid)
            .then((reveals) => {
              if (!mounted) return;
              seedRevealed({
                towerView: reveals.towerView,
                projectDetails: reveals.projectDetails,
              });
            })
            .catch((err) => {
              console.warn('[SuperPickerPublicApp] hydrate reveals failed:', err);
            });
        }
      } catch (err) {
        if (!mounted) return;
        const code = (err as Error & { code?: string })?.code;
        if (code === 'SHARE_EXPIRED') {
          setState({ kind: 'expired' });
          return;
        }
        const reason = err instanceof Error ? err.message : 'Invalid share link';
        setState({ kind: 'denied', reason });
      }
    };

    verifyAccess();
    return () => {
      mounted = false;
    };
    // `seedRevealed` is stable (memoized in the provider), but we still
    // include it for exhaustive-deps.
  }, [seedRevealed]);

  // Auto-kick on trial expiry. We watch the shared `expiresAt` store so
  // any future code path that updates the deadline (e.g. an admin extends
  // the link) flips the recipient back into a granted state without a
  // page reload. The 1s tick keeps the PublicHeader countdown chip in
  // sync as well — it subscribes to the same store via
  // `useSyncExternalStore`.
  useEffect(() => {
    if (state.kind !== 'granted-share') return;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const expiry = getShareExpiry();
      if (expiry != null && Date.now() >= expiry) {
        setState({ kind: 'expired' });
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    const unsubscribe = subscribeShareExpiry(tick);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      unsubscribe();
    };
  }, [state.kind]);

  /**
   * Invoked by `OtpVerificationDialog` after a successful verify. Flips
   * `needsOtp` to false so the page content unmounts the dialog and
   * renders behind it, installs the freshly-issued `expiresAt` into the
   * shared store so the countdown chip starts ticking immediately, and
   * fires the (now-permitted) reveals hydration.
   */
  const handleOtpActivated = useCallback(
    (result: { activatedAt: string; expiresAt: string | null }) => {
      const expiryMs = result.expiresAt ? Date.parse(result.expiresAt) : NaN;
      setShareExpiry(Number.isFinite(expiryMs) ? expiryMs : null);
      setState((prev) =>
        prev.kind === 'granted-share' ? { ...prev, needsOtp: false } : prev,
      );
      // Hydrate reveals now that the share is activated. Best-effort.
      // We pull the uuid from the closure'd `state` via a setState
      // callback above; here we read it once from the current snapshot
      // since the function is invoked synchronously from the dialog.
      if (state.kind === 'granted-share') {
        // Fire the deferred landing event now that the recipient is
        // properly inside the experience (OTP just passed).
        logShareLandingViewed({
          module: state.module,
          projectId: state.projectId,
        });
        fetchExistingReveals(state.uuid)
          .then((reveals) => {
            seedRevealed({
              towerView: reveals.towerView,
              projectDetails: reveals.projectDetails,
            });
          })
          .catch((err) => {
            console.warn(
              '[SuperPickerPublicApp] post-activation hydrate reveals failed:',
              err,
            );
          });
      }
    },
    [state, seedRevealed],
  );

  if (state.kind === 'checking') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">Checking access…</Typography>
      </Box>
    );
  }

  if (state.kind === 'missing-uuid') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Alert severity="info" sx={{ maxWidth: 480 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            No share link provided.
          </Typography>
          <Typography variant="body2">
            This page is only accessible through a personalised link from your agent.
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (state.kind === 'denied') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Alert severity="error" sx={{ maxWidth: 480 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            This link is no longer available.
          </Typography>
          <Typography variant="body2">
            The share link may have expired or been revoked. Please ask your agent for a new link.
          </Typography>
        </Alert>
        <InvitingAgentCard />
      </Box>
    );
  }

  if (state.kind === 'unauthorized-module') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 520, width: '100%' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            Unauthorized
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This share link is bound to a module this app cannot
            display.
          </Typography>
        </Alert>
        <InvitingAgentCard />
      </Box>
    );
  }

  if (state.kind === 'revoked') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 480, width: '100%' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            This link has been revoked.
          </Typography>
          <Typography variant="body2">
            Your agent has revoked access to this share link. Please reach
            out to them below if you'd like a new one.
          </Typography>
        </Alert>
        <InvitingAgentCard />
      </Box>
    );
  }

  if (state.kind === 'expired') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Alert severity="warning" sx={{ maxWidth: 480, width: '100%' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            Your trial period has ended.
          </Typography>
          <Typography variant="body2">
            This share link was valid for a limited preview window which has
            now elapsed. Please contact your agent to request a new link.
          </Typography>
        </Alert>
        <InvitingAgentCard />
      </Box>
    );
  }

  // Recipient hasn't completed WhatsApp OTP yet — render only the
  // verification dialog over a neutral background. We deliberately do
  // NOT mount the bound page in this branch because every API call it
  // would make is gated behind `requireShareAccess`, which 403s with
  // `SHARE_NOT_ACTIVATED` until verification completes.
  if (state.needsOtp) {
    return (
      <>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'var(--color-background, #f5f5f5)',
          }}
        >
          <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
            <CircularProgress size={20} sx={{ mb: 1 }} />
            <Typography variant="body2">Awaiting verification…</Typography>
          </Box>
        </Box>
        <OtpVerificationDialog
          open
          uuid={state.uuid}
          mobileMasked={state.mobileMasked}
          onActivated={handleOtpActivated}
        />
      </>
    );
  }

  // `granted-share` — recipients are pinned to exactly one module. Pass a
  // no-op `onBack` and hide every navigation control that would take them
  // away from the bound page (we keep the buttons in the tree behind
  // `display: none` so the original code paths remain reachable for the
  // agent-side flows that still mount these pages).
  if (state.module === 'PICKER_PROJECT_DETAILS') {
    if (!state.projectId) {
      return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <Alert severity="error" sx={{ maxWidth: 480 }}>
            This project-details link is missing its project reference.
          </Alert>
        </Box>
      );
    }
    return (
      <ProjectDetailPage
        projectId={state.projectId}
        onBack={() => {
          /* recipient cannot navigate out of the bound page */
        }}
        hideBackButton
      />
    );
  }
  if (state.module === 'PICKER_PROJECT_RESEARCH') {
    return (
      <ProjectResearchPage
        onBack={() => {
          /* recipient cannot navigate out of the bound page */
        }}
        hideBackButton
      />
    );
  }
  // PICKER_SITE_PLANS — full list flow (drilling into a project from here is fine
  // because that navigation stays within the share-granted scope). The
  // Project Research entry-point is hidden because the share grant covers
  // Site Plans only.
  return <SitePlansPage hideProjectResearchButton />;
}

export default SuperPickerPublicApp;
